import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationStart, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';

/** GET /api/membership/status — brand-neutral view of the WPGH Club membership. */
interface MembershipStatus {
  membership_status: string | null;
  active: boolean;
  paid_through: string | null;
  plan: string | null;
  /** The old administration fee was turned into this membership. */
  converted_from_admin_fee: boolean;
  had_admin_fee: boolean;
  admin_fee_paid_until: string | null;
  /** Where the membership is sold — Global Health, not this brand. */
  purchase_url: string | null;
}
import {
  AlertModule,
  ButtonModule,
  CardModule,
  GridModule,
  SpinnerModule,
  TableModule
} from '@coreui/angular-pro';
import { IconDirective, IconModule } from '@coreui/icons-angular';
import { UserService, type DashboardStats } from 'app';
import { AuthService } from 'auth';
import { CardAccountService, CrossLoginComponent, CrossLoginService, MyCardsDashboardLinkComponent, NewsletterOptinComponent, ReferralLinkComponent, UserPoolService, WpcpDashboardWidgetComponent } from 'credo';
import { CommunityDashboardWidgetComponent } from 'community';
import { I18nService, TranslatePipe, TranslateService, TranslationModule } from 'i18n';
import { ImageLink, Media, MediaCardComponent, MediaLink, MediaService, MediaViewerOverlayService, PdfMedia } from 'media';
import { CardAccount, Logger, type User, type UserPool } from 'shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { filter } from 'rxjs';

@Component({
  selector: 'main-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    AlertModule,
    ButtonModule,
    CardModule,
    GridModule,
    IconModule,
    SpinnerModule,
    TableModule,
    TranslationModule,
    TranslatePipe,
    ReferralLinkComponent,
    CrossLoginComponent,
    NewsletterOptinComponent,
    MyCardsDashboardLinkComponent,
    MediaCardComponent,
    IconDirective,
    CommunityDashboardWidgetComponent,
    WpcpDashboardWidgetComponent
  ],
  templateUrl: './main-page.component.html',
  styleUrls: ['./main-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainPageComponent {
  protected logger = new Logger('MainPageComponent');

  private userService = inject(UserService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private userPoolService = inject(UserPoolService);
  private cardAccountService = inject(CardAccountService);
  private mediaService = inject(MediaService);
  private mediaViewer = inject(MediaViewerOverlayService);
  private i18n = inject(I18nService);
  private router = inject(Router);
  private http = inject(HttpClient);
  private crossLogin = inject(CrossLoginService);
  private destroyRef = inject(DestroyRef);

  // ────────────────────────────────────────────────
  // State signals
  // ────────────────────────────────────────────────
  user = signal<User | null>(null);
  stats = signal<DashboardStats | null>(null);
  userPools = signal<UserPool[]>([]);
  userCards = signal<CardAccount[]>([]);
  domainId = signal<number | null>(null);
  lic1Active = signal<boolean>(false);

  loading = signal(true);
  poolsLoading = signal(false);
  cardsLoading = signal(false);
  error = signal<string | null>(null);

  // Email stats
  confirmedEmailsCount = signal(0);
  poolsRequiringEmails = signal(0);
  requiredEmailsCount = signal(0);

  // Latest media
  media = signal<Media | null>(null);

  // WPGH Club membership. The administration fee that used to be shown here is
  // retired (v4.0) — the Club membership replaced it as the only membership, and it
  // is bought on Global Health, not here.
  membership = signal<MembershipStatus | null>(null);

  // Presale countdown (ends Jan 31, 2026 23:59 EST)
  presaleEndDate = new Date('2026-01-31T23:59:00-05:00');
  countdownDays = signal(0);
  countdownHours = signal(0);
  countdownMinutes = signal(0);
  countdownSeconds = signal(0);
  presaleEnded = signal(false);

  currentLang = signal<string>('en');

  // PDF modal state
  pdfModalVisible = signal(false);
  pdfModalTitle = signal('');
  pdfUrl = signal<string | null>(null);

  constructor() {
    // Language sync
    effect(() => {
      this.currentLang.set(this.i18n.getLanguage());
    });

    // Load all data
    this.loadUser();
    this.loadMembershipStatus();
    this.loadDashboardStats();
    this.loadUserPools();
    this.loadUserCards();
    this.loadLatestInfoMedia();
    this.loadDomainInfo();

    // Countdown
    effect((onCleanup) => {
      this.updateCountdown();
      const interval = setInterval(() => this.updateCountdown(), 1000);
      onCleanup(() => clearInterval(interval));
    });
  }

  // ────────────────────────────────────────────────
  // Data loading methods
  // ────────────────────────────────────────────────
  private loadUser(): void {
    this.authService.refreshUser()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(user => {
          this.user.set(user);
        });
  }

  private loadMembershipStatus(): void {
    this.http.get<MembershipStatus>('/membership/status')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (m) => this.membership.set(m),
          error: () => this.membership.set(null)
        });
  }

  /**
   * Hop to Global Health, signed in. The membership is sold there, not here, and
   * both brands share the user tenant — so the member should land logged in rather
   * than at a login form. Falls back to the plain URL if the handoff fails.
   */
  goToGlobalHealth(): void {
    this.crossLogin.go('wolfpack-globalhealth', this.membership()?.purchase_url ?? undefined);
  }

  /** Coverage end, or null while nothing is on file. */
  membershipPaidThrough(): Date | null {
    const t = this.membership()?.paid_through;
    return t ? new Date(t) : null;
  }

  private loadDashboardStats(): void {
    this.loading.set(true);
    this.error.set(null);
    this.userService.getDashboardStats()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: data => {
            this.stats.set(data);
            this.loading.set(false);
          },
          error: err => {
            this.logger.error('Dashboard stats error:', err);
            this.error.set(err.error?.error || 'Failed to load dashboard statistics');
            this.loading.set(false);
          }
        });
  }

  private loadUserPools(): void {
    this.poolsLoading.set(true);
    this.userPoolService.getMyPools()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.userPools.set(response.user_pools.sort((a, b) => b.pool_id - a.pool_id));
            this.confirmedEmailsCount.set(response.confirmed_emails_count);
            this.poolsRequiringEmails.set(response.pools_requiring_emails);
            this.requiredEmailsCount.set(response.required_emails_count);
            this.poolsLoading.set(false);
          },
          error: err => {
            this.logger.error('User pools error:', err);
            this.poolsLoading.set(false);
          }
        });
  }

  private loadUserCards(): void {
    this.cardsLoading.set(true);
    this.cardAccountService.getMyCardAccounts()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.userCards.set(response.card_accounts);
            this.cardsLoading.set(false);
          },
          error: err => {
            this.logger.error('User cards error:', err);
            this.cardsLoading.set(false);
          }
        });
  }

  private loadLatestInfoMedia(): void {
    this.mediaService
      .loadMedia(this.i18n.getLanguage(), 'info', '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(items => {
        if (!items?.length) {
          this.media.set(null);
          return;
        }

        const latest = items.reduce((prev, curr) => {
          const prevTime = prev.timeUpdated ?? prev.timeCreated ?? 0;
          const currTime = curr.timeUpdated ?? curr.timeCreated ?? 0;
          return currTime > prevTime ? curr : prev;
        });

        this.media.set(latest);
      });
  }

  private loadDomainInfo(): void {
    this.http.get<{ domain_id: number; name: string; lic_1_active: boolean }>('/auth/domain_info')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.domainId.set(res.domain_id);
          this.lic1Active.set(res.lic_1_active);
        },
        error: err => {
          this.logger.error('Domain info error:', err);
          this.domainId.set(null);
          this.lic1Active.set(false);
        }
      });
  }

  // ────────────────────────────────────────────────
  // Countdown
  // ────────────────────────────────────────────────
  private updateCountdown(): void {
    const distance = this.presaleEndDate.getTime() - Date.now();
    if (distance < 0) {
      this.presaleEnded.set(true);
      return;
    }

    this.countdownDays.set(Math.floor(distance / (1000 * 60 * 60 * 24)));
    this.countdownHours.set(Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    this.countdownMinutes.set(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)));
    this.countdownSeconds.set(Math.floor((distance % (1000 * 60)) / 1000));
  }

  // ────────────────────────────────────────────────
  // PDF & Media
  // ────────────────────────────────────────────────
  openPdfModal(type: 'provisions' | 'license' | 'cards'): void {
    let pdfPath = '';
    let titleKey = '';
    
    if (type === 'provisions') {
      pdfPath = this.currentLang() === 'de'
        ? '/assets/commisison_overview_1.0_de.pdf'
        : '/assets/commisison_overview_1.0_en.pdf';
      titleKey = 'dashboard_pdf_provisions_title';
    } else if (type === 'license') {
      pdfPath = this.currentLang() === 'de'
        ? '/assets/FAC3LESS_Pool-Lizenzsystem_1.3.pdf'
        : '/assets/FAC3LESS_PoolLicenseSystem_1.3_en.pdf';
      titleKey = 'dashboard_pdf_license_title';
    } else if (type === 'cards') {
      pdfPath = this.currentLang() === 'de'
        ? '/assets/card_overview_1.0_de.pdf'
        : '/assets/card_overview_1.0_en.pdf';
      titleKey = 'dashboard_pdf_cards_title';
    }

    if (pdfPath) {
      this.pdfModalTitle.set(this.translate.translate(titleKey));

      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const pdfLink: MediaLink = { src: `${baseUrl}${pdfPath}`, mimeType: 'application/pdf' };
      const coverLink: ImageLink = {
        src: `${window.location.origin}/assets/images/default-media.jpg`,
        mimeType: 'image/jpg',
        width: 0,
        height: 0
      };

      const media: PdfMedia = {
        id: '0',
        name: 'default',
        type: 'pdf',
        cover: coverLink,
        links: [pdfLink],
        timeUpdated: Date.now(),
        trackReactions: false
      };

      this.openMediaViewer(media);
    }
  }

  openMediaViewer(media: Media): void {
    this.mediaViewer.open({ media }).then(overlayRef => {
      this.router.events.pipe(
        filter(event => event instanceof NavigationStart),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        overlayRef.destroy();
      });
  
      overlayRef.onDestroy.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.logger.debug('Media viewer overlay destroyed');
      });
  
      overlayRef.onAction.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(({ action, parameter }) => {
        if (action === 'played') {
          const playedMedia = parameter as Media;
          if (playedMedia.trackReactions) {
            this.mediaService.setReaction(playedMedia.id, 'played')
              .then(() => this.loadLatestInfoMedia());
          }
        }
      });
    }).catch(err => {
      this.logger.error('Failed to open media viewer:', err);
    });
  }

  // ────────────────────────────────────────────────
  // Formatting & helpers (unchanged)
  // ────────────────────────────────────────────────
  formatCurrency(value: number): string {
    return new Intl.NumberFormat(this.currentLang(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value) + ' USD';
  }

  // Currency-aware amount for the per-currency eWallet balance display.
  formatBalance(amount: number, currency: string): string {
    const n = new Intl.NumberFormat(this.currentLang(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount ?? 0);
    // USDT balances are displayed as USD (1:1)
    const display = currency === 'USDT' ? 'USD' : currency;
    return display === 'USD' ? `$${n}` : `${n} ${display}`;
  }

  // Only currencies the user actually holds (non-zero). Empty → show "-".
  nonZeroBalances(user: User | null): { currency: string; balance: number }[] {
    return (user?.balances ?? []).filter(b => Number(b.balance) !== 0);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString(this.currentLang(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  getPercentage(pool: UserPool): number {
    return pool.max_balance === 0 ? 0 : Math.round((pool.turnover / pool.max_balance) * 100);
  }

  hasPoolsWithEmailNotOk(): boolean {
    return this.userPools().some(p => p.needs_email && !p.has_emails);
  }

  getCardsWithoutInitialPayment(): number {
    return this.userCards().filter(c => !c.initialpayment_done).length;
  }

  getCardTypeLabel(cardType: string): string {
    const labels: Record<string, string> = {
      silber: this.translate.translate('dashboard_card_type_silber'),
      gold: this.translate.translate('dashboard_card_type_gold'),
      platin: this.translate.translate('dashboard_card_type_platin'),
      m3: this.translate.translate('dashboard_card_type_m3')
    };
    return labels[cardType] || cardType;
  }

  getCardTypeBadgeColor(cardType: string): string {
    const colors: Record<string, string> = {
      silber: 'secondary',
      gold: 'warning',
      platin: 'info',
      m3: 'primary'
    };
    return colors[cardType] || 'dark';
  }

  getLevelBadgeUrl(level: number): string {
    return `/assets/images/badge${level || 0}.png`;
  }

  getLevelName(level: number): string {
    const names: Record<number, string> = {
      0: this.translate.translate('dashboard_level_0_name'),
      1: this.translate.translate('dashboard_level_1_name'),
      2: this.translate.translate('dashboard_level_2_name'),
      3: this.translate.translate('dashboard_level_3_name'),
      4: this.translate.translate('dashboard_level_4_name'),
      5: this.translate.translate('dashboard_level_5_name'),
      6: this.translate.translate('dashboard_level_6_name'),
      7: this.translate.translate('dashboard_level_7_name', { appName: this.translate.translate('app.name') })
    };
    return names[level] || this.translate.translate('dashboard_level_unknown');
  }

  getLevelDescription(level: number): string {
    const desc: Record<number, string> = {
      0: this.translate.translate('dashboard_level_0_desc'),
      1: this.translate.translate('dashboard_level_1_desc'),
      2: this.translate.translate('dashboard_level_2_desc'),
      3: this.translate.translate('dashboard_level_3_desc'),
      4: this.translate.translate('dashboard_level_4_desc'),
      5: this.translate.translate('dashboard_level_5_desc'),
      6: this.translate.translate('dashboard_level_6_desc'),
      7: this.translate.translate('dashboard_level_7_desc')
    };
    return desc[level] || this.translate.translate('dashboard_level_unknown_desc');
  }

  onBadgeImageError(event: Event): void {
    (event.target as HTMLImageElement).src = '/assets/images/badge0.png';
  }

  formatMembershipDate(): string {
    const date = this.membershipPaidThrough();
    if (!date) return '';
    return date.toLocaleDateString(this.currentLang(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  }
}